import { create } from "zustand";
import { GridSizeDataType } from "./useDashboardStore";
import {
  ColumnDataType,
  DropDownListOptionType,
} from "types/common/CommonType";
import { IndexObjectType } from "components/dataGrid/LibraryDataGrip";
import { Util } from "Util";
import { ColumnType } from "types/project/ProjectRequestBodyType";

export interface ColumnInfoType {
  columnInfoId: string;
  columnName: string;
  columnOrder: number;
  columnType: ColumnType;
  hiddenCheck?: boolean;
  originalColumnName: string;
  tableName?: string;
}

export interface StepSortListType {
  columnName: string;
  sortType: "desc" | "asc";
  sortOrder: number;
}
export interface RowDataType {
  [key: string | number]: IndexObjectType[];
}

export interface CacheInfoType {
  completedStepOrder: number;
  totalStepCount: number;
  cacheStatus?: "COMPLETED_CACHING" | "RUNNING_CACHING" | "START_CACHING";
  projectId?: string;
  projectVersion?: string;
}

interface TableDataStoreType {
  projectAccessPermission: boolean;
  lastProjectVersion: string | null;
  tableLoadingCount: number;
  dataLockMode: boolean;
  isStepSortMode: boolean;
  rowsPerPage: number;
  currentPage: number;
  displayedPages: number[];
  rowData: RowDataType;
  columnData: ColumnDataType[];
  originColumnInfo: ColumnInfoType[];
  currentColumnInfo: ColumnInfoType[];
  selectColumnList: DropDownListOptionType[];
  stepSortList: StepSortListType[];
  isSorting: boolean;
  isFilters: boolean;
  isColumns: boolean;
  isFullScreen: boolean;
  isSearchColumn: boolean;
  isHideColumn: boolean;
  isDisableGridRefresh: boolean;
  searchColumnName: string;
  columnsInExpression: string[];
  browserFind: boolean;
  totalSize: GridSizeDataType;
  dataTrigger: number;
  dataAbortTrigger: number;
  isAbortRefetch: boolean;
  scrollTrigger: number;
  sizingTrigger: number;
  onlyVisibleColumn: { name: string; isDbClick: boolean };
  computeTargetColumn: string;
  computeTargetColumnTrigger: number;
  previousCacheKey: string | null;
  initFilterList: boolean;
  ignoreHiddenColumnUpdate: boolean;
  isProjectCachingState: boolean;
  projectCacheInfo: CacheInfoType;
  currentApiStepOrderNum: string | null;
  setProjectAccessPermission: (permission: boolean) => void;
  setLastProjectVersion: (version: string) => void;
  startTableLoading: () => void;
  stopTableLoading: () => void;
  setDataLockMode: (value: boolean) => void;
  setIsStepSortMode: (value: boolean) => void;
  setCurrentPage: (page: number) => void;
  setDisplayedPages: (pages: number[]) => void;
  setRowData: (newData?: IndexObjectType[], pageIndex?: number) => void;
  updateRowData: (newData: RowDataType) => void;
  setColumnData: (newData: ColumnDataType[]) => void;
  setOriginColumnInfo: (newData: ColumnInfoType[]) => void;
  setCurrentColumnInfo: (newData: ColumnInfoType[]) => void;
  setSelectColumnList: (newData: DropDownListOptionType[]) => void;
  setStepSortList: (newData: StepSortListType[]) => void;
  setIsSorting: (value: boolean) => void;
  setIsFilters: (value: boolean) => void;
  setIsColumns: (value: boolean) => void;
  setIsFullScreen: (value: boolean) => void;
  setIsSearchColumn: (value: boolean) => void;
  setIsHideColumn: (value: boolean) => void;
  setIsDisableGridRefresh: (value: boolean) => void;
  setBrowserFind: (browserFind: boolean) => void;
  setSearchColumnName: (columnName: string) => void;
  setColumnsInExpression: (columnName: string[]) => void;
  getDataTrigger: (notUpdateFilter?: boolean) => void;
  getDataAbortTrigger: (refetch?: boolean) => void;
  tableSizingTrigger: () => void;
  scrollToTopTrigger: () => void;
  setOnlyVisibleColumn: (column: { name: string; isDbClick: boolean }) => void;
  setComputeTargetColumn: (column: string) => void;
  setTotalSize: (totalData: GridSizeDataType) => void;
  setPreviousCacheKey: (cacheKey: string | null) => void;
  setIgnoreHiddenColumnUpdate: (value: boolean) => void;
  setIsProjectCachingState: (value: boolean) => void;
  setProjectCacheInfo: (cacheInfo: CacheInfoType) => void;
  setCurrentApiStepOrderNum: (value: string | null) => void;
  resetTableData: () => void;
}

const useTableDataStore = create<TableDataStoreType>((set) => ({
  projectAccessPermission: true,
  lastProjectVersion: null,
  dataLockMode: true,
  tableLoadingCount: 0,
  isStepSortMode: false,
  rowsPerPage: 200,
  currentPage: 1,
  displayedPages: [1],
  rowData: {},
  columnData: [],
  originColumnInfo: [],
  currentColumnInfo: [],
  selectColumnList: [],
  stepSortList: [],
  isSorting: false,
  isFilters: false,
  isColumns: false,
  isFullScreen: false,
  isSearchColumn: false,
  isHideColumn: true,
  isDisableGridRefresh: false,
  columnsInExpression: [],
  searchColumnName: "",
  browserFind: false,
  totalSize: {
    columns: 0,
    rows: 0,
    filterRows: 0,
  },
  dataTrigger: 0,
  isAbortRefetch: false,
  dataAbortTrigger: 0,
  sortTrigger: 0,
  sizingTrigger: 0,
  scrollTrigger: 0,
  onlyVisibleColumn: { name: "", isDbClick: false },
  computeTargetColumn: "",
  computeTargetColumnTrigger: 0,
  previousCacheKey: null,
  initFilterList: true,
  ignoreHiddenColumnUpdate: false,
  isProjectCachingState: false,
  projectCacheInfo: { completedStepOrder: 0, totalStepCount: 0 },
  currentApiStepOrderNum: null,
  setProjectAccessPermission: (permission: boolean) => {
    set({ projectAccessPermission: permission });
  },
  setLastProjectVersion: (version: string) => {
    set({ lastProjectVersion: version });
  },
  startTableLoading: () => {
    set((state) => ({
      tableLoadingCount: state.tableLoadingCount + 1,
    }));
  },
  stopTableLoading: () => {
    set((state) => ({
      tableLoadingCount: Math.max(state.tableLoadingCount - 1, 0),
    }));
  },
  setDataLockMode: (value: boolean) => {
    set({ dataLockMode: value });
  },
  setIsStepSortMode: (value: boolean) => {
    set({ isStepSortMode: value });
  },
  setCurrentPage: (page: number) => {
    set({ currentPage: page });
  },
  setDisplayedPages: (pages: number[]) => {
    set((state) => {
      if (!Util.isEqualArray(pages, state.displayedPages)) {
        return { ...state, displayedPages: pages };
      } else {
        return { ...state };
      }
    });
  },
  setupRowData: (totalRows: number, pageIndex: number) => {
    set((state) => {
      let defaultSizeArray = Array(
        // state.rowsPerPage + state.rowsPerPage * 2
        state.rowsPerPage
      ).fill(null);
      return { rowData: { [pageIndex]: defaultSizeArray } };
    });
  },
  setRowData: (newData?: IndexObjectType[], pageIndex?: number) => {
    if (newData && pageIndex) {
      set({ rowData: { [pageIndex]: newData } });
      // if (init) {
      //   return { rowData: { [pageIndex]: newData } };
      // } else {
      //   const updatedRowData = { ...state.rowData, [pageIndex]: newData };
      //   const maxSize = 10;
      //   const keys = Object.keys(state.rowData);
      //   // 키의 개수를 10개로 제한
      //   if (keys.length >= maxSize) {
      //     const oldestKey = Number(keys[0]);
      //     delete updatedRowData[oldestKey];
      //   }

      //   return { rowData: updatedRowData };
      // }
    } else {
      set({ rowData: {} });
    }
  },
  updateRowData: (newData: RowDataType) => {
    set((state) => {
      const updatedRowData = { ...state.rowData, ...newData };
      // const maxSize = 10;
      // const keys = Object.keys(state.rowData);
      // // 키의 개수를 10개로 제한
      // if (keys.length >= maxSize) {
      //   const oldestKey = Number(keys[0]);
      //   delete updatedRowData[oldestKey];
      // }

      return { rowData: updatedRowData };
    });
  },
  setColumnData: (newData: ColumnDataType[]) => {
    set({ columnData: newData });
  },
  setOriginColumnInfo: (newData: ColumnInfoType[]) => {
    set({ originColumnInfo: newData });
  },
  setCurrentColumnInfo: (newData: ColumnInfoType[]) => {
    set({ currentColumnInfo: newData });
  },
  setSelectColumnList: (newData: DropDownListOptionType[]) => {
    set({ selectColumnList: newData });
  },
  setStepSortList: (newData: StepSortListType[]) => {
    set({ stepSortList: newData });
  },
  setIsSorting: (value: boolean) => {
    set({ isSorting: value });
  },
  setIsFilters: (value: boolean) => {
    set({ isFilters: value, isColumns: false });
  },
  setIsColumns: (value: boolean) => {
    set({
      isColumns: value,
    });
  },
  setIsFullScreen: (value: boolean) => {
    set({ isFullScreen: value });
  },
  setIsSearchColumn: (value: boolean) => {
    set({ isSearchColumn: value });
  },
  setIsHideColumn: (value: boolean) => {
    set({ isHideColumn: value });
  },
  setIsDisableGridRefresh: (value: boolean) => {
    set({ isDisableGridRefresh: value });
  },
  setSearchColumnName: (columnName: string) => {
    set({ searchColumnName: columnName });
  },
  setColumnsInExpression: (columnName: string[]) => {
    set({ columnsInExpression: columnName });
  },
  setBrowserFind: (browserFind: boolean) => {
    set({ browserFind: browserFind });
  },
  setTotalSize: (totalData: GridSizeDataType) => {
    set({ totalSize: totalData });
  },
  getDataTrigger: (notUpdateFilter?: boolean) => {
    set((state) => ({
      ...state,
      dataTrigger: state.dataTrigger + 1,
      initFilterList: notUpdateFilter ? false : true,
    }));
  },
  getDataAbortTrigger: (refetch?: boolean) => {
    set((state) => ({
      ...state,
      dataAbortTrigger: state.dataAbortTrigger + 1,
      isAbortRefetch: refetch || false,
    }));
  },
  tableSizingTrigger: () => {
    set((state) => ({ ...state, sizingTrigger: state.sizingTrigger + 1 }));
  },
  setOnlyVisibleColumn: (column: { name: string; isDbClick: boolean }) => {
    set({ onlyVisibleColumn: column });
  },
  scrollToTopTrigger: () => {
    set((state) => ({ scrollTrigger: state.scrollTrigger + 1 }));
  },
  setComputeTargetColumn: (column: string) => {
    set((state) => ({
      computeTargetColumn: column,
      computeTargetColumnTrigger: state.computeTargetColumnTrigger + 1,
    }));
  },
  setPreviousCacheKey: (cacheKey: string | null) => {
    set({ previousCacheKey: cacheKey });
  },
  setIgnoreHiddenColumnUpdate: (value: boolean) => {
    set({ ignoreHiddenColumnUpdate: value });
  },
  setIsProjectCachingState: (value: boolean) => {
    set({ isProjectCachingState: value });
  },
  setProjectCacheInfo: (cacheInfo: CacheInfoType) => {
    set((state) => {
      if (Util.isEqual(state.projectCacheInfo, cacheInfo)) {
        return state;
      }

      return { projectCacheInfo: cacheInfo };
    });
  },
  setCurrentApiStepOrderNum: (value: string | null) => {
    set({ currentApiStepOrderNum: value });
  },
  resetTableData: () => {
    set({
      projectAccessPermission: true,
      isProjectCachingState: false,
      lastProjectVersion: null,
      dataLockMode: true,
      tableLoadingCount: 0,
      isStepSortMode: false,
      rowsPerPage: 200,
      currentPage: 1,
      displayedPages: [1],
      rowData: {},
      columnData: [],
      originColumnInfo: [],
      currentColumnInfo: [],
      stepSortList: [],
      isSorting: false,
      isFilters: false,
      isColumns: false,
      isFullScreen: false,
      isDisableGridRefresh: false,
      isSearchColumn: false,
      initFilterList: true,
      searchColumnName: "",
      totalSize: { columns: 0, rows: 0, filterRows: 0 },
      dataTrigger: 0,
      dataAbortTrigger: 0,
      isAbortRefetch: false,
      sizingTrigger: 0,
      scrollTrigger: 0,
      previousCacheKey: null,
      onlyVisibleColumn: { name: "", isDbClick: false },
      ignoreHiddenColumnUpdate: false,
      currentApiStepOrderNum: null,
    });
  },
}));

export default useTableDataStore;
